import React, { useState, useEffect } from 'react';
import { invoiceService, authService } from '../services/api';
import CostBreakdown from './CostBreakdown';

const Dashboard = () => {
  const [billingPeriods, setBillingPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);
  const [error, setError] = useState(null);
  
  // Filter state for org tokens
  const [selectedPeriodFilters, setSelectedPeriodFilters] = useState([]);
  const [selectedTeamFilters, setSelectedTeamFilters] = useState([]);
  const [collapsedYears, setCollapsedYears] = useState(new Set());
  
  // Billing Insights mode state
  const [useBillingInsights, setUseBillingInsights] = useState(false);
  const [accountUrn, setAccountUrn] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [insightsData, setInsightsData] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  
  // Fetch account information on mount (skip for org tokens)
  useEffect(() => {
    const fetchAccountInfo = async () => {
      try {
        // Check auth method first - org tokens cannot access account endpoint
        const { authMethod } = await authService.checkAuthStatus();
        if (authMethod === 'org-token') {
          // Org tokens don't have access to account info
          return;
        }
        
        const data = await authService.getAccountInfo();
        setAccountInfo(data.account);
      } catch (error) {
        // Handle 403 specifically for org tokens
        if (error.response?.status === 403) {
          console.log('Account info not available for organization tokens');
        } else {
          console.error('Error fetching account info:', error);
        }
      }
    };

    fetchAccountInfo();
  }, []);

  // Listen for billing mode changes from Navigation component
  // Also load initial state from localStorage to stay in sync with Navigation
  useEffect(() => {
    const checkBillingMode = () => {
      const saved = localStorage.getItem('useBillingInsights');
      setUseBillingInsights(saved === 'true');
    };
    
    // Load initial state from localStorage to sync with Navigation
    checkBillingMode();
    
    // Listen for changes when Navigation toggles billing mode
    window.addEventListener('billingModeChanged', checkBillingMode);
    
    return () => {
      window.removeEventListener('billingModeChanged', checkBillingMode);
    };
  }, []);

  // Load saved billing insights settings and auto-populate team UUID
  useEffect(() => {
    if (useBillingInsights) {
      const savedAccountUrn = localStorage.getItem('billingInsightsAccountUrn');
      const savedStartDate = localStorage.getItem('billingInsightsStartDate');
      const savedEndDate = localStorage.getItem('billingInsightsEndDate');
      
      // Auto-populate Account URN with team UUID if available
      if (accountInfo?.team?.uuid) {
        const autoUrn = `do:team:${accountInfo.team.uuid}`;
        setAccountUrn(autoUrn);
      } else if (savedAccountUrn) {
        // Only use saved value if we don't have team info
        setAccountUrn(savedAccountUrn);
      }
      
      if (savedStartDate) setStartDate(savedStartDate);
      if (savedEndDate) setEndDate(savedEndDate);
    }
  }, [useBillingInsights, accountInfo]);

  useEffect(() => {
    if (!useBillingInsights) {
      const fetchBillingPeriods = async () => {
        try {
          const periods = await invoiceService.getBillingPeriods();
          setBillingPeriods(periods);
          
          // Initialize filters: select all periods by default
          const uniquePeriods = [...new Set(periods.map(p => p.period))];
          setSelectedPeriodFilters(uniquePeriods);
          
          // Initialize team filters: select all teams by default
          const uniqueTeams = [...new Set(periods.map(p => p.account_urn).filter(Boolean))];
          setSelectedTeamFilters(uniqueTeams);
          
          // Initialize collapsed years: collapse all except most recent
          const periodsByYear = {};
          uniquePeriods.forEach(period => {
            const year = period.split('-')[0];
            if (!periodsByYear[year]) {
              periodsByYear[year] = [];
            }
            periodsByYear[year].push(period);
          });
          
          const years = Object.keys(periodsByYear).sort((a, b) => b.localeCompare(a));
          if (years.length > 1) {
            // Collapse all years except the most recent
            const yearsToCollapse = years.slice(1);
            setCollapsedYears(new Set(yearsToCollapse));
          }
          
          setLoading(false);
        } catch (error) {
          console.error('Error fetching billing periods:', error);
          setError('Failed to load billing periods. Please try again.');
          setLoading(false);
        }
      };
      
      fetchBillingPeriods();
    } else {
      setLoading(false);
    }
  }, [useBillingInsights]);
  
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!selectedPeriod) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const data = await invoiceService.getInvoiceData(selectedPeriod);

        console.log(`Received ${data.length} invoice items. First row:`, data[0]);

        if (!data || data.length === 0) {
          setError('The invoice data appears to be empty or invalid.');
          setLoading(false);
          return;
        }

        setInvoiceData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching invoice data:', error);
        setError('Failed to load invoice data. Please try again.');
        setLoading(false);
      }
    };
    
    fetchInvoiceData();
  }, [selectedPeriod]);

  // Toggle period filter
  const togglePeriodFilter = (period) => {
    setSelectedPeriodFilters(prev => {
      if (prev.includes(period)) {
        return prev.filter(p => p !== period);
      } else {
        return [...prev, period];
      }
    });
  };

  // Toggle team filter
  const toggleTeamFilter = (teamUrn) => {
    setSelectedTeamFilters(prev => {
      if (prev.includes(teamUrn)) {
        return prev.filter(t => t !== teamUrn);
      } else {
        return [...prev, teamUrn];
      }
    });
  };

  // Get filtered invoices
  const getFilteredInvoices = () => {
    return billingPeriods.filter(invoice => {
      const periodMatch = selectedPeriodFilters.length === 0 || selectedPeriodFilters.includes(invoice.period);
      const teamMatch = selectedTeamFilters.length === 0 || selectedTeamFilters.includes(invoice.account_urn);
      return periodMatch && teamMatch;
    });
  };

  // Extract unique periods and teams
  const getUniquePeriods = () => {
    const periods = [...new Set(billingPeriods.map(p => p.period))];
    return periods.sort((a, b) => b.localeCompare(a)); // Most recent first
  };

  const getUniqueTeams = () => {
    return [...new Set(billingPeriods.map(p => p.account_urn).filter(Boolean))];
  };

  // Format team URN for display
  const formatTeamUrn = (urn) => {
    if (!urn) return 'Unknown';
    const match = urn.match(/do:(team|teamgroup):(.+)/);
    if (match) {
      const [, type, id] = match;
      return `${type === 'teamgroup' ? 'Organization' : 'Team'}: ${id.substring(0, 8)}...`;
    }
    return urn;
  };

  // Group periods by year
  const getPeriodsByYear = () => {
    const periods = getUniquePeriods();
    const grouped = {};
    
    periods.forEach(period => {
      const year = period.split('-')[0];
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(period);
    });
    
    return grouped;
  };

  // Toggle year collapse
  const toggleYearCollapse = (year) => {
    setCollapsedYears(prev => {
      const newSet = new Set(prev);
      if (newSet.has(year)) {
        newSet.delete(year);
      } else {
        newSet.add(year);
      }
      return newSet;
    });
  };

  // Fetch billing insights data
  const fetchBillingInsights = async () => {
    if (!accountUrn || !startDate || !endDate) {
      setError('Please provide Account URN, Start Date, and End Date');
      return;
    }

    setLoading(true);
    setError(null);
    setInsightsData(null);

    try {
      // Only save Account URN to localStorage if it's not the auto-generated one
      const autoUrn = accountInfo?.team?.uuid ? `do:team:${accountInfo.team.uuid}` : null;
      if (accountUrn !== autoUrn) {
        localStorage.setItem('billingInsightsAccountUrn', accountUrn);
      } else {
        // Remove from localStorage if using auto-generated value
        localStorage.removeItem('billingInsightsAccountUrn');
      }
      
      // Always save date settings
      localStorage.setItem('billingInsightsStartDate', startDate);
      localStorage.setItem('billingInsightsEndDate', endDate);

      // Fetch all pages
      let allDataPoints = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const data = await invoiceService.getBillingInsights(
          accountUrn,
          startDate,
          endDate,
          currentPage,
          100
        );

        allDataPoints = [...allDataPoints, ...data.data_points];
        totalPages = data.total_pages;
        currentPage++;
      } while (currentPage <= totalPages);

      console.log(`Fetched ${allDataPoints.length} billing insights data points`);
      
      // Transform billing insights data to match invoice data format
      const transformedData = transformBillingInsightsToInvoiceFormat(allDataPoints);
      setInvoiceData(transformedData);
      setInsightsData(allDataPoints);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching billing insights:', error);
      setError(error.response?.data?.message || 'Failed to load billing insights. Please check your Account URN and try again.');
      setLoading(false);
    }
  };

  // Transform billing insights data to invoice CSV format
  const transformBillingInsightsToInvoiceFormat = (dataPoints) => {
    return dataPoints.map(point => ({
      product: point.description || 'Unknown',
      start: point.start_date,
      end: point.start_date, // Insights are daily, so start = end
      USD: point.total_amount,
      resourceId: point.sku || '',
      resourceName: point.group_description || '',
      projectName: '',
      region: point.region || ''
    }));
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-cf-gray-900 mb-2">Invoice Analysis</h1>
          <p className="text-sm text-cf-gray-600">
            {useBillingInsights 
              ? (
                <>
                  View billing insights for organizations and teams.{' '}
                  <a 
                    href="https://docs.digitalocean.com/reference/api/digitalocean/#operation/billingInsights_list" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-do-blue hover:text-do-blue-hover underline"
                  >
                    API Reference
                  </a>
                </>
              )
              : 'View and analyze your DigitalOcean costs by service type and billing period'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-white border border-cf-gray-300 rounded-cf-md text-cf-gray-800">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-cf-gray-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        {useBillingInsights ? (
          // Billing Insights Mode
          <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 mb-6 shadow-cf">
            <h2 className="text-lg font-medium text-cf-gray-900 mb-4">Billing Insights</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="account-urn" className="block text-sm font-medium text-cf-gray-700 mb-2">
                  Account URN <span className="text-red-500">*</span>
                </label>
                <input
                  id="account-urn"
                  type="text"
                  value={accountUrn}
                  onChange={(e) => setAccountUrn(e.target.value)}
                  placeholder="do:team:12345678-1234-1234-1234-123456789012"
                  className="block w-full px-3 py-2 border border-cf-gray-300 rounded-cf text-sm text-cf-gray-800 focus:outline-none focus:border-do-blue focus:ring-1 focus:ring-do-blue transition-colors"
                />
                <p className="mt-1 text-xs text-cf-gray-500">
                  {accountInfo?.team?.uuid ? (
                    <>Auto-filled with your team UUID. Format: do:team:uuid or do:teamgroup:uuid</>
                  ) : (
                    <>Format: do:team:uuid or do:teamgroup:uuid</>
                  )}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="start-date" className="block text-sm font-medium text-cf-gray-700 mb-2">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="block w-full px-3 py-2 border border-cf-gray-300 rounded-cf text-sm text-cf-gray-800 focus:outline-none focus:border-do-blue focus:ring-1 focus:ring-do-blue transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="end-date" className="block text-sm font-medium text-cf-gray-700 mb-2">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="block w-full px-3 py-2 border border-cf-gray-300 rounded-cf text-sm text-cf-gray-800 focus:outline-none focus:border-do-blue focus:ring-1 focus:ring-do-blue transition-colors"
                  />
                </div>
              </div>

              <div className="pt-2">
                <button
                  onClick={fetchBillingInsights}
                  disabled={loading || !accountUrn || !startDate || !endDate}
                  className="w-full md:w-auto px-6 py-2 bg-do-blue text-white rounded-cf text-sm font-medium hover:bg-do-blue-hover disabled:bg-cf-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Loading...' : 'Fetch Billing Insights'}
                </button>
              </div>

              <div className="mt-4 p-3 bg-do-blue-50 border border-do-blue-200 rounded-cf">
                <p className="text-xs text-cf-gray-700">
                  <strong>Note:</strong> Billing insights provide day-over-day changes in resource usage. 
                  The date range cannot exceed 31 days. Daily resource usage may not reflect month-end billing totals.
                  {' '}
                  <a 
                    href="https://docs.digitalocean.com/reference/api/digitalocean/#operation/billingInsights_list" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-do-blue hover:text-do-blue-hover underline"
                  >
                    View API Reference
                  </a>
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Invoice Mode
          <>
            {selectedPeriod ? (
              // Invoice Detail View - Show back button
              <div className="mb-6">
                <button
                  onClick={() => {
                    setSelectedPeriod(null);
                    setInvoiceData(null);
                  }}
                  className="flex items-center text-sm text-cf-gray-700 hover:text-cf-gray-900 transition-colors"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Invoice Selection
                </button>
              </div>
            ) : (
              // Invoice Selection View - Show filters and invoice list
              <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 mb-6 shadow-cf">
                {loading ? (
                  <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-cf-gray-100 rounded w-32"></div>
                    <div className="flex gap-2">
                      <div className="h-8 bg-cf-gray-100 rounded w-24"></div>
                      <div className="h-8 bg-cf-gray-100 rounded w-24"></div>
                      <div className="h-8 bg-cf-gray-100 rounded w-24"></div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Period Filters Grouped by Year */}
                    {Object.keys(getPeriodsByYear()).length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-sm font-medium text-cf-gray-700 mb-3">Filter by Date Range</h3>
                        <div className="space-y-3">
                          {Object.keys(getPeriodsByYear()).sort((a, b) => b.localeCompare(a)).map(year => {
                            const isCollapsed = collapsedYears.has(year);
                            const periods = getPeriodsByYear()[year];
                            
                            return (
                              <div key={year} className="rounded-cf">
                                <button
                                  onClick={() => toggleYearCollapse(year)}
                                  className="w-full flex items-center justify-between px-4 py-2 hover:bg-cf-gray-50 transition-colors"
                                >
                                  <span className="font-medium text-cf-gray-800">{year}</span>
                                  <svg
                                    className={`w-5 h-5 text-cf-gray-500 transition-transform ${
                                      isCollapsed ? '' : 'rotate-90'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                                {!isCollapsed && (
                                  <div className="px-4 pb-3 flex flex-wrap gap-2">
                                    {periods.map(period => (
                                      <button
                                        key={period}
                                        onClick={() => togglePeriodFilter(period)}
                                        className={`px-4 py-2 rounded-cf text-sm font-medium transition-colors ${
                                          selectedPeriodFilters.includes(period)
                                            ? 'bg-do-blue text-white hover:bg-do-blue-hover'
                                            : 'bg-cf-gray-100 text-cf-gray-700 hover:bg-cf-gray-200 border border-cf-gray-300'
                                        }`}
                                      >
                                        {period}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Team Filters */}
                    {getUniqueTeams().length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-sm font-medium text-cf-gray-700 mb-3">Filter by Team or organization</h3>
                        <div className="flex flex-wrap gap-2">
                          {getUniqueTeams().map(teamUrn => (
                            <button
                              key={teamUrn}
                              onClick={() => toggleTeamFilter(teamUrn)}
                              title={teamUrn}
                              className={`px-4 py-2 rounded-cf text-sm font-medium transition-colors ${
                                selectedTeamFilters.includes(teamUrn)
                                  ? 'bg-do-blue text-white hover:bg-do-blue-hover'
                                  : 'bg-cf-gray-100 text-cf-gray-700 hover:bg-cf-gray-200 border border-cf-gray-300'
                              }`}
                            >
                              {formatTeamUrn(teamUrn)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Invoice List */}
                    <div>
                      <h3 className="text-sm font-medium text-cf-gray-700 mb-3">
                        Invoices ({getFilteredInvoices().length})
                      </h3>
                      {getFilteredInvoices().length === 0 ? (
                        <div className="text-center py-8 text-cf-gray-500 text-sm">
                          No invoices match the selected filters
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {getFilteredInvoices().map(invoice => (
                            <button
                              key={invoice.id}
                              onClick={() => setSelectedPeriod(invoice.id)}
                              className={`w-full text-left px-4 py-3 rounded-cf border transition-colors ${
                                selectedPeriod === invoice.id
                                  ? 'bg-do-blue-50 border-do-blue text-cf-gray-900'
                                  : 'bg-white border-cf-gray-200 hover:bg-cf-gray-50 text-cf-gray-800'
                              }`}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="font-medium text-sm">
                                    {invoice.period}
                                    {invoice.status === 'PENDING' && (
                                      <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                        Pending
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-cf-gray-600 mt-1">
                                    Invoice #{invoice.id}
                                  </div>
                                  {invoice.account_urn && (
                                    <div className="text-xs text-cf-gray-500 mt-1 font-mono">
                                      {formatTeamUrn(invoice.account_urn)}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right">
                                  <div className="font-semibold text-sm">
                                    ${invoice.amount || '0.00'}
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {loading && (useBillingInsights ? insightsData : selectedPeriod) ? (
          <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 shadow-cf">
            <div className="flex justify-center items-center h-64">
              <svg className="animate-spin h-8 w-8 text-do-blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          </div>
        ) : invoiceData && invoiceData.length > 0 ? (
          <CostBreakdown 
            invoiceData={invoiceData} 
            selectedInvoice={selectedPeriod ? billingPeriods.find(inv => inv.id === selectedPeriod) : null}
          />
        ) : (useBillingInsights ? insightsData !== null : selectedPeriod) && !loading ? (
          <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 text-center shadow-cf">
            <p className="text-sm text-cf-gray-600">No data available for this period.</p>
          </div>
        ) : null}
    </div>
  );
};

export default Dashboard;